import { memo, useRef, useState, type DragEvent, type JSX, type KeyboardEvent } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { Id } from '../../../core/model';
import { Icon } from '../../components/Icon';
import { InlineEdit } from '../../components/InlineEdit';
import type { MenuEntry } from '../../components/menuModel';
import { useActions, useEditedSceneId, useProject, useRuntime, useUiContext } from '../../context';
import { attempt, safely } from '../../safe';
import { dropPositionAt, neighbour, reorderIndex, type DropPosition } from '../listMath';
import '../panels.css';

const DRAG_TYPE = 'application/x-studio-scene';

interface RowProps {
  sceneId: Id;
  selected: boolean;
  renaming: boolean;
  drop: DropPosition | null;
  onRenameDone(): void;
  onActivate(id: Id): void;
  onMenu(id: Id, x: number, y: number): void;
  onStartRename(id: Id): void;
  onDragOverRow(id: Id, position: DropPosition | null): void;
  onDropRow(draggedId: Id, targetId: Id, position: DropPosition): void;
}

const SceneRow = memo(function SceneRow(props: RowProps): JSX.Element {
  const { sceneId, selected, renaming, drop } = props;
  const { studio } = useUiContext();
  const name = useProject((p) => p.scenes[sceneId]?.name ?? '');
  const studioMode = useRuntime((r) => r.studioMode);
  const isProgram = useRuntime((r) => r.programSceneId === sceneId);
  const isPreview = useRuntime((r) => r.studioMode && r.previewSceneId === sceneId);

  const positionOf = (event: DragEvent<HTMLLIElement>): DropPosition => {
    const rect = event.currentTarget.getBoundingClientRect();
    return dropPositionAt(event.clientY - rect.top, rect.height, false);
  };

  return (
    <li
      id={`scene-row-${sceneId}`}
      role="option"
      aria-selected={selected}
      className={['ui-list-row', selected ? 'selected' : '', drop ? `drop-${drop}` : ''].join(' ')}
      draggable={!renaming}
      onClick={(e) => {
        // The second click of a double-click starts a rename; the scene is already selected.
        if (e.detail < 2) props.onActivate(sceneId);
      }}
      onDoubleClick={() => props.onStartRename(sceneId)}
      onContextMenu={(e) => {
        e.preventDefault();
        props.onMenu(sceneId, e.clientX, e.clientY);
      }}
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_TYPE, sceneId);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        if (!Array.from(e.dataTransfer.types).includes(DRAG_TYPE)) return;
        e.preventDefault();
        props.onDragOverRow(sceneId, positionOf(e));
      }}
      onDragLeave={() => props.onDragOverRow(sceneId, null)}
      onDrop={(e) => {
        e.preventDefault();
        const dragged = e.dataTransfer.getData(DRAG_TYPE);
        const position = positionOf(e);
        if (dragged && position !== 'inside') props.onDropRow(dragged, sceneId, position);
      }}
    >
      <span className="kind-icon">
        <Icon name="layers" />
      </span>
      {renaming ? (
        <InlineEdit
          initial={name}
          label="Scene name"
          onSubmit={(value) => {
            attempt(studio, () => studio.commands.renameScene(sceneId, value));
            props.onRenameDone();
          }}
          onCancel={props.onRenameDone}
        />
      ) : (
        <span className="ui-grow ui-ellipsis">{name}</span>
      )}
      {studioMode && isPreview ? <span className="ui-badge preview">PVW</span> : null}
      {studioMode && isProgram ? <span className="ui-badge program">PGM</span> : null}
    </li>
  );
});

export function ScenesPanel(): JSX.Element {
  const { studio, ui } = useUiContext();
  const actions = useActions();
  const order = useProject(useShallow((p) => p.sceneOrder));
  const current = useEditedSceneId();
  const [renaming, setRenaming] = useState<Id | null>(null);
  const [drop, setDrop] = useState<{ id: Id; position: DropPosition } | null>(null);
  const list = useRef<HTMLUListElement>(null);

  const activate = (id: Id): void => void safely(studio, () => studio.selectScene(id));
  const move = (id: Id, toIndex: number): void => void attempt(studio, () => studio.commands.moveScene(id, toIndex));
  const duplicate = (id: Id): void => {
    const created = attempt(studio, () => studio.commands.duplicateScene(id));
    if (created) activate(created);
  };

  const menuFor = (id: Id): MenuEntry[] => {
    const index = order.indexOf(id);
    return [
      { label: 'Rename', shortcut: 'F2', onSelect: () => setRenaming(id) },
      { label: 'Duplicate', onSelect: () => duplicate(id) },
      { label: 'Filters', onSelect: () => ui.getState().openDialog({ type: 'filters', owner: { type: 'scene', id } }) },
      { separator: true },
      { label: 'Move up', disabled: index <= 0, onSelect: () => move(id, index - 1) },
      { label: 'Move down', disabled: index >= order.length - 1, onSelect: () => move(id, index + 1) },
      { separator: true },
      { label: 'Remove', danger: true, disabled: order.length <= 1, onSelect: () => actions.removeScene(id) },
    ];
  };

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    if (renaming) return;
    let handled = true;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const next = neighbour(order, current, event.key === 'ArrowDown' ? 1 : -1);
      if (next && next !== current) activate(next);
    } else if (event.key === 'F2' && current) setRenaming(current);
    else if ((event.key === 'Delete' || event.key === 'Backspace') && current) actions.removeScene(current);
    else handled = false;
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const index = current ? order.indexOf(current) : -1;
  return (
    <div className="ui-panel">
      <div className="ui-panel-body">
        <ul
          ref={list}
          className="ui-list"
          role="listbox"
          aria-label="Scenes"
          aria-activedescendant={current ? `scene-row-${current}` : undefined}
          tabIndex={0}
          onKeyDown={onKeyDown}
        >
          {order.map((id) => (
            <SceneRow
              key={id}
              sceneId={id}
              selected={id === current}
              renaming={renaming === id}
              drop={drop?.id === id ? drop.position : null}
              onRenameDone={() => {
                setRenaming(null);
                list.current?.focus();
              }}
              onActivate={activate}
              onStartRename={setRenaming}
              onMenu={(sceneId, x, y) => ui.getState().openMenu({ x, y, items: menuFor(sceneId), returnFocus: list.current })}
              onDragOverRow={(sceneId, position) => setDrop(position ? { id: sceneId, position } : null)}
              onDropRow={(dragged, target, position) => {
                setDrop(null);
                const from = order.indexOf(dragged);
                const over = order.indexOf(target);
                if (from < 0 || over < 0 || dragged === target || position === 'inside') return;
                const to = reorderIndex(from, over, position);
                if (to !== from) move(dragged, to);
              }}
            />
          ))}
        </ul>
      </div>
      <div className="ui-toolbar" role="toolbar" aria-label="Scene actions">
        <button type="button" className="ui-icon-btn" aria-label="Add scene" title="Add scene" onClick={() => actions.addScene()}>
          <Icon name="plus" />
        </button>
        <button
          type="button"
          className="ui-icon-btn"
          aria-label="Remove scene"
          title="Remove scene"
          disabled={!current || order.length <= 1}
          onClick={() => current && actions.removeScene(current)}
        >
          <Icon name="minus" />
        </button>
        <button
          type="button"
          className="ui-icon-btn"
          aria-label="Scene filters"
          title="Scene filters"
          disabled={!current}
          onClick={() => current && ui.getState().openDialog({ type: 'filters', owner: { type: 'scene', id: current } })}
        >
          <Icon name="sliders" />
        </button>
        <span className="ui-grow" />
        <button
          type="button"
          className="ui-icon-btn"
          aria-label="Move scene up"
          title="Move scene up"
          disabled={index <= 0}
          onClick={() => current && move(current, index - 1)}
        >
          <Icon name="up" />
        </button>
        <button
          type="button"
          className="ui-icon-btn"
          aria-label="Move scene down"
          title="Move scene down"
          disabled={index < 0 || index >= order.length - 1}
          onClick={() => current && move(current, index + 1)}
        >
          <Icon name="down" />
        </button>
      </div>
    </div>
  );
}
